import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRightIcon,
  SparklesIcon,
  TruckIcon,
  ShieldCheckIcon,
  ShoppingBagIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import Loading from '../components/common/Loading';
import ProductCard from '../components/product/ProductCard';
import { getStoreSlug, setStoreSlug } from '../utils/storeUtils';

const HomePage = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [store, setStore] = useState(null);
  const [viewMode, setViewMode] = useState('stores'); // 'stores' or 'products'

  useEffect(() => {
    const storeSlug = searchParams.get('store');
    
    if (storeSlug) {
      // View specific store
      setViewMode('products');
      setStoreSlug(storeSlug);
      fetchStoreData(storeSlug);
    } else {
      // View all stores
      setViewMode('stores');
      fetchStoresList();
    }
  }, [searchParams]);

  const fetchStoresList = async () => {
    try {
      const res = await api.get('/stores/');
      setStores(res.data.results || res.data);
    } catch (error) {
      console.error('Error fetching stores:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStoreData = async (storeSlug) => {
    setLoading(true);
    try {
      const storeRes = await api.get(`/stores/${storeSlug}/`);
      setStore(storeRes.data);
      
      const productsRes = await api.get('/products/', {
        params: { store: storeSlug, ordering: '-display_order', limit: 8 }
      });
      setProducts(productsRes.data.results || productsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStoreClick = (storeSlug) => {
    setSearchParams({ store: storeSlug });
  };

  if (loading) {
    return <Loading />;
  }

  const features = [
    {
      icon: TruckIcon,
      title: 'توصيل سريع',
      description: 'توصيل سريع وآمن لجميع أنحاء العراق',
    },
    {
      icon: ShieldCheckIcon,
      title: 'ضمان الجودة',
      description: 'منتجات أصلية 100% مع ضمان الجودة',
    },
    {
      icon: SparklesIcon,
      title: 'عروض مميزة',
      description: 'عروض وخصومات حصرية على منتجاتنا',
    },
  ];

  // Show stores list if no store selected
  if (viewMode === 'stores') {
    return (
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 text-white py-20 md:py-32 overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
            <div className="absolute top-40 right-10 w-72 h-72 bg-purple-400 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
          </div>
          
          <div className="container mx-auto px-4 text-center relative z-10">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 animate-fade-in">
              StoreIQ
            </h1>
            <p className="text-xl md:text-2xl lg:text-3xl mb-8 text-gray-100 max-w-3xl mx-auto animate-fade-in-delay">
              منصة متاجر إلكترونية - اختر متجرك المفضل
            </p>
          </div>
        </section>

        {/* Stores List */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
              المتاجر المتاحة
            </h2>
            
            {stores.length === 0 ? (
              <div className="text-center py-12">
                <ShoppingBagIcon className="w-24 h-24 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-xl">لا توجد متاجر متاحة حالياً</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {stores.map((storeItem) => (
                  <div
                    key={storeItem.id}
                    onClick={() => handleStoreClick(storeItem.slug)}
                    className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all transform hover:-translate-y-2 cursor-pointer"
                  >
                    <div className="h-48 bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                      <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                        <ShoppingBagIcon className="w-12 h-12 text-white" />
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">
                        {storeItem.name}
                      </h3>
                      <p className="text-gray-600 mb-4 line-clamp-2">
                        {storeItem.description || 'متجر إلكتروني متكامل'}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">
                          {storeItem.slug}
                        </span>
                        <button className="text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-2">
                          تصفح المتجر
                          <ArrowRightIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    );
  }

  // Show store products if store selected
  return (
    <div className="min-h-screen">
      {/* Back to stores button */}
      <div className="bg-gray-100 py-4">
        <div className="container mx-auto px-4">
          <button
            onClick={() => {
              setSearchParams({});
              setStore(null);
              setProducts([]);
            }}
            className="text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-2"
          >
            ← العودة للمتاجر
          </button>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 text-white py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
          <div className="absolute top-40 right-10 w-72 h-72 bg-purple-400 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
        </div>
        
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 animate-fade-in">
            {store?.name || 'StoreIQ'}
          </h1>
          <p className="text-xl md:text-2xl lg:text-3xl mb-8 text-gray-100 max-w-3xl mx-auto animate-fade-in-delay">
            {store?.description || 'متجرك الإلكتروني في مكان واحد'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in-delay-2">
            <Link
              to={`/products?store=${store?.slug}`}
              className="bg-white text-blue-600 px-8 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-all shadow-2xl transform hover:scale-105 flex items-center gap-2"
            >
              تصفح المنتجات
              <ArrowRightIcon className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="text-center p-6 rounded-2xl bg-gradient-to-br from-gray-50 to-white border border-gray-200 hover:shadow-xl transition-all transform hover:-translate-y-2"
                >
                  <div className="inline-flex p-4 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl mb-4">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      {products.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                المنتجات المميزة
              </h2>
              <Link
                to={`/products?store=${store?.slug}`}
                className="text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-2"
              >
                عرض الكل
                <ArrowRightIcon className="w-5 h-5" />
              </Link>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {products.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            جاهز للتسوق؟
          </h2>
          <p className="text-xl mb-8 text-gray-100">
            تصفح مجموعتنا الواسعة من المنتجات واختر ما يناسبك
          </p>
          <Link
            to={`/products?store=${store?.slug}`}
            className="inline-flex items-center gap-2 bg-white text-blue-600 px-8 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-all shadow-2xl transform hover:scale-105"
          >
            ابدأ التسوق الآن
            <ArrowRightIcon className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
